"use client";

import React from "react";
import { Mail, MapPin, Phone, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-sm border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
export default function ForgingConnections() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="5746" id="handling-request-form" className="dark bg-background text-foreground py-20 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="space-y-12">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter leading-tight uppercase font-sans">
                Handling <span className="text-primary">Request</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-sans">
                Streamline your arrival at N’djili International Airport. Our logistics experts are standing by to ensure seamless ground handling and crew support 24/7.
              </p>
            </div>

            <div className="space-y-8">
              <div className="group">
                <h3 className="text-xs font-mono font-medium text-primary uppercase tracking-widest mb-2">
                  Operations Desk
                </h3>
                <Link to="tel:+243810641198" className="text-3xl md:text-4xl font-bold text-foreground hover:text-primary transition-colors flex items-center gap-4"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone size-8 md:size-10 text-primary drop-shadow-[0_0_8px_rgba(255,191,0,0.3)]" aria-hidden="true" data-ast-id="f:/src/pages/Contact/sections/ForgingConnections.tsx|sig:ktnie2" data-ast-start="3101" data-ast-end="3195"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg>+243 840 161 875</Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-8 border-t border-border/30">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary mb-1">
                    <Mail className="size-5" />
                    <span className="font-bold text-xs uppercase tracking-wider font-mono">Direct Email</span>
                  </div>
                  <Link to="mailto:ddchambangi@gmail.com" className="block text-lg text-foreground hover:text-primary transition-colors font-sans">
                    ddchambangi@gmail.com
                  </Link>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-primary mb-1">
                    <MapPin className="size-5" />
                    <span className="font-bold text-xs uppercase tracking-wider font-mono">Location</span>
                  </div>
                  <address className="not-italic text-lg text-muted-foreground font-sans">
                    N’djili International Airport,<br />
                    Kinshasa, DR Congo
                  </address>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card/40 backdrop-blur-md border border-primary/10 rounded-sm p-6 md:p-10 shadow-2xl">
            <div className="mb-8 border-l-4 border-primary pl-6">
              <h3 className="text-2xl font-bold mb-2 uppercase tracking-tight">Request Logistics</h3>
              <p className="text-muted-foreground text-sm">
                Submit your flight details for immediate coordination with our ground handling crew.
              </p>
            </div>

            <FormProvider formKey="handling_request_form_contact" formId="handling_request_form_contact" sectionName="handling-request-form" defaultValues={{
            name: "",
            email: "",
            company: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            company: {
              required: false
            },
            phone: {
              required: false,
              pattern: "phone"
            },
            message: {
              required: true
            }
          }} submitText="SUBMIT REQUEST" successMessage="Request received. Our operations team will contact you shortly with confirmation." errorMessage="Submission failed. Please contact our 24/7 desk directly by phone.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
          <Mail className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h4 className="text-2xl font-bold text-foreground uppercase tracking-tight">Request Sent</h4>
          <p className="text-muted-foreground max-w-xs mx-auto text-sm">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-sm">
          New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Contact name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase text-[10px] font-bold tracking-widest text-muted-foreground">Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="Captain or Ops Manager" className="rounded-sm border-border/50 bg-background/50 focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase text-[10px] font-bold tracking-widest text-muted-foreground">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ops@airline.com" className="rounded-sm border-border/50 bg-background/50 focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase text-[10px] font-bold tracking-widest text-muted-foreground">Airline / Operator</FormLabel>
              <FormControl>
                <Input placeholder="Global Cargo Airways" className="rounded-sm border-border/50 bg-background/50 focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase text-[10px] font-bold tracking-widest text-muted-foreground">Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+243 000 000 000" className="rounded-sm border-border/50 bg-background/50 focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please provide flight details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase text-[10px] font-bold tracking-widest text-muted-foreground">Flight Details & Required Services</FormLabel>
            <FormControl>
              <Textarea placeholder="Call sign, aircraft type, ETA/ETD, and required ground services (Fuel, Catering, Handling)..." className="resize-none rounded-sm border-border/50 bg-background/50 focus:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-xs font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-bold tracking-widest uppercase bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl shadow-primary/10 transition-all hover:scale-[1.01] rounded-sm">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Transmitting...
          </> : <>
            SUBMIT REQUEST
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}